import { create } from 'zustand'
import type { FocusAssetId, Loan, LoanStage, PersonaId, PipelineMetrics, ProductType, AssetClass } from '@/types'
import { PERSONAS_BY_ASSET } from '@/config/focusWorkbench'

interface PipelineFilters {
  stage?: LoanStage
  product?: ProductType
  propertyType?: AssetClass
  assignee?: string
  amountMin?: number
  amountMax?: number
}

interface AppState {
  selectedLoan: Loan | null
  setSelectedLoan: (loan: Loan | null) => void

  selectedDealId: string | null
  setSelectedDealId: (id: string | null) => void

  metrics: PipelineMetrics | null
  setMetrics: (m: PipelineMetrics) => void

  sidebarCollapsed: boolean
  toggleSidebar: () => void

  /** Dashboards nav group. Open by default — the boards are the first thing
   *  their owners look at, so the group must not need a click to be found. */
  dashboardsNavOpen: boolean
  toggleDashboardsNav: () => void

  activeModule: string
  setActiveModule: (module: string) => void

  focusAsset: FocusAssetId
  setFocusAsset: (asset: FocusAssetId) => void

  persona: PersonaId
  setPersona: (persona: PersonaId) => void

  pipelineFilters: PipelineFilters
  setPipelineFilters: (filters: Partial<PipelineFilters>) => void
  clearPipelineFilters: () => void

  activeDealTab: 'summary' | 'cashflows' | 'structure' | 'documents' | 'memos'
  setActiveDealTab: (tab: AppState['activeDealTab']) => void
}

export const useAppStore = create<AppState>((set) => ({
  selectedLoan: null,
  setSelectedLoan: (loan) => set({ selectedLoan: loan }),

  selectedDealId: null,
  setSelectedDealId: (selectedDealId) => set({ selectedDealId }),

  metrics: null,
  setMetrics: (metrics) => set({ metrics }),

  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  dashboardsNavOpen: true,
  toggleDashboardsNav: () => set((s) => ({ dashboardsNavOpen: !s.dashboardsNavOpen })),

  activeModule: 'command-center',
  setActiveModule: (activeModule) => set({ activeModule }),

  focusAsset: 'cre',
  setFocusAsset: (focusAsset) =>
    set((state) => {
      const allowedPersonas = PERSONAS_BY_ASSET[focusAsset]
      return {
        focusAsset,
        persona: allowedPersonas.includes(state.persona) ? state.persona : allowedPersonas[0],
      }
    }),

  persona: 'originator',
  setPersona: (persona) => set({ persona }),

  pipelineFilters: {},
  setPipelineFilters: (filters) =>
    set((state) => ({
      pipelineFilters: { ...state.pipelineFilters, ...filters },
    })),
  clearPipelineFilters: () => set({ pipelineFilters: {} }),

  activeDealTab: 'summary',
  setActiveDealTab: (activeDealTab) => set({ activeDealTab }),
}))
